import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Repeat } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cad } from "@/components/sponsored/format";
import { CampaignSettingsCard } from "@/components/sponsored/CampaignSettingsCard";
import { BudgetCard } from "@/components/sponsored/BudgetCard";
import { KpiStrip } from "@/components/sponsored/KpiStrip";
import { TransactionsTable } from "@/components/sponsored/TransactionsTable";
import { LedgerTable } from "@/components/sponsored/LedgerTable";
import { PolicyCard } from "@/components/sponsored/PolicyCard";
import { InsightsCard } from "@/components/sponsored/InsightsCard";

const OFFER = {
  live: ["Live — returning shoppers see your offer", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  campaign_off: ["Off", "bg-slate-50 text-slate-600 border-slate-200"],
  budget_unavailable: ["Paused — top up your budget to show it", "bg-amber-50 text-amber-800 border-amber-200"],
  rate_not_above_base: ["Paused — pick a rate above Cyannect's base", "bg-amber-50 text-amber-800 border-amber-200"],
};

function useTopupReturn(load) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const sid = params.get("topup_session");
    if (params.get("topup_cancelled")) { toast("Card payment cancelled. Nothing was charged."); setParams({}, { replace: true }); return; }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/merchant/sponsored-cashback/topups/${sid}`);
        if (r.data.status === "PAID") { toast.success(`${cad(r.data.amount_cad)} added to your budget`); load(); setParams({}, { replace: true }); return; }
        if (r.data.status !== "INITIATED") { toast.error("That payment didn't go through."); setParams({}, { replace: true }); return; }
      } catch { /* retried below */ }
      if (++tries < 8) setTimeout(poll, 2000); else toast("Payment is still processing. Your budget updates once Stripe confirms it.");
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

function Header({ d }) {
  const [label, cls] = OFFER[d?.offer_state] || [null, ""];
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="pf-chip pf-chip-mint"><Repeat className="w-3 h-3" /> Sponsored Cashback</span>
        <span className="pf-chip" data-testid="sc-preview-chip">Preview only</span>
        {label && <span className={`inline-flex px-3 py-1 rounded-full border text-xs font-semibold ${cls}`} data-testid="sc-offer-state">{label}</span>}
      </div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Bring first-time shoppers back</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">After a shopper's first verified sale with you, offer a higher total cashback on their next purchase. Cyannect keeps funding its base cashback; you fund the extra from a prepaid budget.</p>
    </div>
  );
}

function Upsell({ d }) {
  return (
    <div className="pf-card p-6 max-w-xl" data-testid="sc-plan-upsell">
      <h3 className="font-display text-xl font-medium">Included with Starter and Pro</h3>
      <p className="text-sm text-slate-600 mt-1">You're on {d.plan_label || "Basic"}. Change your plan to offer Sponsored Cashback to returning shoppers.</p>
      <Link to="/merchant/billing" className="pf-btn-primary inline-block mt-4 px-5 py-2.5 rounded-full text-sm" data-testid="sc-upgrade-link">See plans</Link>
    </div>
  );
}

export default function MerchantSponsoredCashback() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get("/merchant/sponsored-cashback").then((r) => setD(r.data))
    .catch((e) => setErr(e.response?.status === 404 ? "off" : "error")), []);
  useEffect(() => { load(); }, [load]);
  useTopupReturn(load);
  if (err === "off") return <p className="text-sm text-slate-600" data-testid="sc-unavailable">Sponsored Cashback isn't available for your account yet.</p>;
  if (err) return <p className="text-sm text-rose-600" data-testid="sc-error">Couldn't load Sponsored Cashback. Please refresh.</p>;
  if (!d) return <p className="text-sm text-slate-500" data-testid="sc-loading">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="sponsored-cashback-page">
      <Header d={d} />
      {!d.plan_eligible ? <Upsell d={d} /> : (
        <>
          <KpiStrip k={d.kpis} />
          <div className="grid lg:grid-cols-5 gap-6 items-start">
            <div className="lg:col-span-3 space-y-6">
              <CampaignSettingsCard key={d.campaign.version} d={d} onSaved={load} />
              <PolicyCard policy={d.performance_fee_policy} />
            </div>
            <div className="lg:col-span-2"><BudgetCard d={d} onChange={load} /></div>
          </div>
          <InsightsCard />
          <section className="pf-card p-6" data-testid="sc-reports-card">
            <h3 className="font-display text-xl font-medium text-slate-900 mb-4">Reports and reconciliation</h3>
            <Tabs defaultValue="purchases">
              <TabsList><TabsTrigger value="purchases" data-testid="sc-tab-purchases">Repeat purchases</TabsTrigger><TabsTrigger value="ledger" data-testid="sc-tab-ledger">Budget activity</TabsTrigger></TabsList>
              <TabsContent value="purchases" className="pt-4"><TransactionsTable /></TabsContent>
              <TabsContent value="ledger" className="pt-4"><LedgerTable /></TabsContent>
            </Tabs>
          </section>
        </>
      )}
      {!d.plan_eligible && <PolicyCard policy={d.performance_fee_policy} />}
    </div>
  );
}
